import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getToken } from "@/lib/api";
import { Loader2, Send, Link2, Download, Ban, CheckCircle2, Mail, Copy, ShieldCheck, X } from "lucide-react";

const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-600", pending_approval: "bg-amber-100 text-amber-700",
  approved: "bg-cobalt/10 text-cobalt", sent: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700", opened: "bg-emerald-100 text-emerald-700",
  clicked: "bg-emerald-100 text-emerald-700", downloaded: "bg-emerald-100 text-emerald-700",
  expired: "bg-slate-100 text-slate-500", failed: "bg-red-100 text-red-700", recalled: "bg-red-100 text-red-700",
};
const STAGES = ["draft", "pending_approval", "approved", "sent", "delivered", "opened", "clicked", "downloaded", "expired", "failed", "recalled"];

export default function DeliveryTab() {
  const [data, setData] = useState(null);
  const [emailOn, setEmailOn] = useState(false);
  const [open, setOpen] = useState(null); // expanded comm
  const [share, setShare] = useState({}); // shareResult by id

  const load = useCallback(async () => {
    const [{ data: list }, { data: si }] = await Promise.all([
      api.get("/communications"), api.get("/sending-identity").catch(() => ({ data: {} })),
    ]);
    setData(list); setEmailOn(!!si.email_configured);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

  const act = async (id, fn) => { try { await fn(); toast.success("Done"); await load(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const approve = (id) => act(id, () => api.post(`/communications/${id}/approve`));
  const send = (id) => act(id, () => api.post(`/communications/${id}/send`));
  const revoke = (id) => act(id, () => api.post(`/communications/${id}/revoke`));
  const genLink = async (id) => {
    try {
      const { data: r } = await api.put(`/communications/${id}/share`, { expires_in_days: 7 });
      const url = `${window.location.origin}/share/${r.share_token}`;
      setShare({ ...share, [id]: url }); toast.success("Secure link ready");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const zipUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/communications/${id}/download-zip?auth=${getToken()}`;

  return (
    <div data-testid="delivery-tab">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
        {["draft", "pending_approval", "approved", "sent", "opened", "downloaded"].map((s) => (
          <div key={s} className="bg-white rounded-xl border border-slate-100 p-3 text-center" data-testid={`dl-kpi-${s}`}>
            <div className="text-xl font-extrabold text-navy">{data.counts?.[s] || 0}</div>
            <div className="text-[10px] uppercase text-slate-400">{s.replace(/_/g, " ")}</div>
          </div>
        ))}
      </div>

      {!emailOn && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-700 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Live email is off until a Resend key + verified domain are added. Secure share links + ZIP downloads work now — send from your own office if preferred.
        </div>
      )}

      {data.rows.length === 0 ? <p className="text-sm text-slate-400">No communications yet. Prepare one from a case's Voice Timeline.</p> : (
        <div className="space-y-2">
          {data.rows.map((c) => (
            <div key={c.communication_id} className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`dl-row-${c.communication_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap cursor-pointer" onClick={() => setOpen(open === c.communication_id ? null : c.communication_id)}>
                <div className="min-w-0">
                  <div className="font-semibold text-navy flex items-center gap-2 flex-wrap">{c.type_label}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[c.status] || "bg-slate-100"}`}>{c.status.replace(/_/g, " ")}</span>
                    <span className="text-xs text-slate-400 font-normal">{c.case_number}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{c.subject} · {(c.recipients || []).map((r) => r.email).join(", ") || "no recipients"}</div>
                  <div className="text-[10px] text-slate-400">{c.attorney_name} · {new Date(c.created_at).toLocaleString()} · {c.package_id}</div>
                </div>
              </div>

              {open === c.communication_id && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-3" data-testid={`dl-detail-${c.communication_id}`}>
                  <div className="text-[11px] font-mono text-slate-400 break-all">SHA-256: {c.content_hash}</div>
                  <div className="flex flex-wrap gap-2">
                    {["draft", "pending_approval"].includes(c.status) && (
                      <button onClick={() => approve(c.communication_id)} data-testid={`dl-approve-${c.communication_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-xs font-medium hover:bg-cobalt-hover"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                    )}
                    {c.status === "approved" && (
                      <button onClick={() => send(c.communication_id)} disabled={!emailOn} title={emailOn ? "" : "Email not configured"} data-testid={`dl-send-${c.communication_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 disabled:opacity-40"><Send className="w-3.5 h-3.5" /> Send email</button>
                    )}
                    <button onClick={() => genLink(c.communication_id)} data-testid={`dl-link-${c.communication_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"><Link2 className="w-3.5 h-3.5" /> Secure link</button>
                    <a href={zipUrl(c.communication_id)} target="_blank" rel="noreferrer" data-testid={`dl-zip-${c.communication_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"><Download className="w-3.5 h-3.5" /> Download ZIP</a>
                    {c.status !== "recalled" && <button onClick={() => revoke(c.communication_id)} data-testid={`dl-revoke-${c.communication_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-red-200 text-xs text-red-500 hover:bg-red-50"><Ban className="w-3.5 h-3.5" /> Revoke</button>}
                  </div>
                  {share[c.communication_id] && (
                    <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
                      <input readOnly value={share[c.communication_id]} className="flex-1 bg-transparent text-xs text-slate-600 outline-none" data-testid={`dl-shareurl-${c.communication_id}`} />
                      <button onClick={() => { navigator.clipboard.writeText(share[c.communication_id]); toast.success("Copied"); }} className="p-1.5 rounded-full hover:bg-slate-200 text-slate-500"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  {c.tracking?.opened_at && <div className="text-[11px] text-emerald-600">Opened {new Date(c.tracking.opened_at).toLocaleString()}{c.tracking.downloaded_at ? ` · Downloaded ${new Date(c.tracking.downloaded_at).toLocaleString()}` : ""}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
